import { create } from "zustand";
import { persist, createJSONStorage } from 'zustand/middleware';
import { CompanyTreeNodeDto } from "../models/company-tree-node.dto";
import { randomHexColors } from "../helpers/appHelpers";

type LevelColor = { 
    parendId: number | null; 
    levelColor: string 
}

export type TreeNode = {
    id: number;
    parentId: number | null;
    expanded: boolean;
    data: CompanyTreeNodeDto;
    children?: TreeNode[];
    "1"?: string;
}

type useOrganizationTreeStore = {
    organizationTree: TreeNode[];
    treeSourceData: TreeNode[];
    levelColors: LevelColor[];
    generateTree: (treeNodes: CompanyTreeNodeDto) => void;
    clearOrgTreeStore: () => void;
}

export const useOrganizationTreeStore = create(
    persist<useOrganizationTreeStore>(
        (set, get) => ({
            organizationTree: [],
            treeSourceData: [],
            levelColors: [],
            generateTree: (parentNode: CompanyTreeNodeDto) => {
                const allNodes = extractAllNodes(parentNode);
                const rootNode = generateTree(parentNode);
                const levelColors = getLevelColors(allNodes);

                set((state) => ({
                    ...state,
                    levelColors,
                    organizationTree: [rootNode],
                    treeSourceData: allNodes,
                }))
            },
            clearOrgTreeStore: () => {
                set((state) => ({
                    ...state,
                    organizationTree: [],
                    treeSourceData: [],
                    levelColors: [],
                }));
            }
        }),
        {
            name: 'organization-tree',
            storage: createJSONStorage(() => sessionStorage),
        }
    )
);

/**
 * Recursively transforms a CompanyTreeNodeDto into a TreeNode structure.
 * This function takes a company tree node and converts it into a tree structure
 * by processing all child nodes and maintaining parent-child relationships.
 * 
 * @param treeNode - The company tree node to be transformed
 * @returns A TreeNode object containing the processed tree structure
 * 
 * @example
 * const companyNode: CompanyTreeNodeDto = {
 *   id: "1",
 *   parentId: "0",
 *   children: []
 * };
 * const treeNode = generateTree(companyNode);
 */
const generateTree = (treeNode: CompanyTreeNodeDto) => {
    const { children: baseChildNodes, ...otherValues } = treeNode;

    let children: TreeNode[] = []

    if (baseChildNodes && baseChildNodes.length) {
        baseChildNodes.forEach(childNode => {
            const node = generateTree(childNode);
            children = children.concat([node]);
        });
    }

    let node: TreeNode = {
        id: treeNode.id,
        parentId: treeNode.parentId,
        expanded: true,
        data: otherValues,
        children
    }

    return node;
};

/**
 * Recursively extracts all nodes from a company tree structure into a flat array.
 * Each node in the resulting array maintains its hierarchical relationship through parentId
 * while being transformed into a TreeNode format with expanded state.
 * 
 * @param node - The root company tree node to extract from
 * @returns An array of TreeNode objects representing all nodes in the tree
 * 
 * @example
 * const companyNode = {
 *   id: 1,
 *   parentId: null,
 *   name: "Company",
 *   children: [
 *     { id: 2, parentId: 1, name: "Department", children: [] }
 *   ]
 * };
 * const allNodes = extractAllNodes(companyNode);
 */
const extractAllNodes = (node: CompanyTreeNodeDto) => {
    const { children, ...otherValues } = node;

    let allNodes: TreeNode[] = [
        {
            id: node.id,
            parentId: node.parentId,
            expanded: true,
            data: otherValues,
            children: []
        }
    ];

    if (children && children.length) {
        children.forEach(childNode => {
            allNodes = allNodes.concat(extractAllNodes(childNode));
        });
    }

    return allNodes
};

/**
 * Generates color assignments for different levels in a tree structure based on parent IDs.
 * Each unique parent ID is assigned a random color from a predefined color palette.
 * 
 * @param treeNodes - Array of tree nodes containing parent-child relationships
 * @returns Array of level color assignments mapping parent IDs to specific colors
 * 
 * @example
 * const nodes = [
 *   { id: 1, parentId: null },
 *   { id: 2, parentId: 1 },
 *   { id: 3, parentId: 1 }
 * ];
 * const colors = getLevelColors(nodes);
 * // Returns: [{ parentId: null, levelColor: "#..." }, { parentId: 1, levelColor: "#..." }]
 */
const getLevelColors = (treeNodes: TreeNode[]) => {
    const nodeParentIds = new Set<number | null>();
    treeNodes.forEach((node) => nodeParentIds.add(node.parentId));

    const levelColors: LevelColor[] = Array.from(nodeParentIds).map((parendId, index) => {
        return {
            parendId, 
            levelColor: randomHexColors[index]
        }
    })

    return levelColors;
};
